import type { ReactNode } from 'react';

/**
 * Shared hero block used at the top of landing pages (Brief, Digital, Stocks).
 * Gives each page the same editorial opening: eyebrow → giant display title →
 * short lede → right-aligned side slot (window tabs, stats).
 *
 * The side rule on `.editorial-card` class keeps the accent but avoids the
 * heavy top stripe that fought with the nav underline.
 */
type Props = {
  eyebrow: string;
  title: ReactNode;
  lede?: ReactNode;
  side?: ReactNode;
  stats?: ReactNode;
};

export function PageHero({ eyebrow, title, lede, side, stats }: Props) {
  return (
    <section className="editorial-card" aria-labelledby="page-hero">
      <span className="eyebrow">{eyebrow}</span>
      <h1 id="page-hero" className="editorial-display mt-2">
        {title}
      </h1>
      {(lede || side) && (
        <div className="mt-5 flex flex-wrap items-end justify-between gap-5">
          {lede && (
            <p className="text-muted max-w-[54ch] text-[0.95rem] leading-snug">{lede}</p>
          )}
          {side}
        </div>
      )}
      {stats && <div className="mt-5 pt-5 border-t border-border">{stats}</div>}
    </section>
  );
}

export function KpiStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: 'accent' | 'default';
}) {
  return (
    <div>
      <div className="text-[0.62rem] uppercase tracking-[0.22em] text-muted font-(family-name:--font-ui) font-semibold mb-1">
        {label}
      </div>
      <div
        className={`font-(family-name:--font-heading) text-2xl md:text-3xl font-bold tabular-nums leading-none ${
          tone === 'accent' ? 'text-accent' : 'text-text'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
